import { useEffect, useRef } from "react";

/**
 * Handle annotation saved/canceled custom DOM events from the annotation flow.
 *
 * The listeners are bound ONCE per viewerType. The callbacks are read through
 * refs so passing inline functions (a new identity each render) doesn't cause the
 * effect to re-run — re-running used to tear down the in-progress preview, which
 * made a freshly drawn shape disappear immediately.
 *
 * @param {string} viewerType - 'histology' or 'macro'
 * @param {React.MutableRefObject} drawingRef - ref to the drawing state object
 * @param {Function} setIsDrawingMode - state setter for drawing mode
 * @param {Function} cleanupRectangle - callback to clean up the temp preview
 */
const useAnnotationEvents = (viewerType, drawingRef, setIsDrawingMode, cleanupRectangle) => {
  const setDrawRef = useRef(setIsDrawingMode);
  const cleanupRef = useRef(cleanupRectangle);
  setDrawRef.current = setIsDrawingMode;
  cleanupRef.current = cleanupRectangle;

  useEffect(() => {
    const handle = () => {
      const drawing = drawingRef.current;
      if (drawing.rectangle) {
        cleanupRef.current(drawing);
        drawing.rectangle = null;
        drawing.pendingAnnotation = null;
        setDrawRef.current(false);
      }
    };

    document.addEventListener(`${viewerType}-annotation-saved`, handle);
    document.addEventListener(`${viewerType}-annotation-canceled`, handle);
    return () => {
      document.removeEventListener(`${viewerType}-annotation-saved`, handle);
      document.removeEventListener(`${viewerType}-annotation-canceled`, handle);
    };
  }, [viewerType, drawingRef]);
};

export default useAnnotationEvents;
